import { useState, useRef, useEffect } from "react"
import MapGL from "@urbica/react-map-gl"
// @ts-ignore
import Draw from "@urbica/react-map-gl-draw"
import "mapbox-gl/dist/mapbox-gl.css"
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css"
import bbox from "@turf/bbox"
import { useSaleCreateStore } from "../../stores/useSaleCreateStore"
import { MAPBOX_TOKEN, mapStyles } from "../../../../shared/constants"
import mapVisualizationDrawStyles from "../../../../shared/mapVisualizationDrawStyles"

import { SpatialFileUpload } from "./SpatialFileUpload"
import {
  processSpatialFile,
  formatErrorForUser,
} from "../../services/spatialFileService"
import type { SpatialValidationError } from "../../types/spatial"

export const MapBoundaryStep = () => {
  const { saleData, updateBoundary } = useSaleCreateStore()
  const mapRef = useRef<MapGL>(null)

  const [viewport, setViewport] = useState({
    latitude: 39.8283,
    longitude: -98.5795,
    zoom: 4,
  })

  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingStatus, setProcessingStatus] = useState<string>("")
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Modal state for upload and preview
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewData, setPreviewData] = useState<any>(null)

  // Single features array - following MapVisualization pattern
  const [features, setFeatures] = useState<any[]>([])

  // Initialize features from store boundary data on mount only
  useEffect(() => {
    if (saleData.boundary_geom) {
      if (saleData.boundary_geom.type === "Polygon") {
        setFeatures([
          {
            type: "Feature",
            geometry: saleData.boundary_geom,
            properties: { source: "existing" },
          },
        ])
      } else if (saleData.boundary_geom.type === "MultiPolygon") {
        // Convert MultiPolygon to separate Polygon features for editing
        const polygonFeatures = saleData.boundary_geom.coordinates.map(
          (polygonCoords, index) => ({
            type: "Feature",
            geometry: {
              type: "Polygon",
              coordinates: polygonCoords,
            },
            properties: {
              source: "existing",
              polygonIndex: index,
            },
          })
        )
        setFeatures(polygonFeatures)
      }
    }
    // Only run on component mount, not when boundary_geom changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-zoom to existing polygon when component mounts or boundary changes
  useEffect(() => {
    if (saleData.boundary_geom) {
      try {
        // Create a feature for the bbox calculation
        const feature = {
          type: "Feature" as const,
          geometry: saleData.boundary_geom,
          properties: {},
        }

        // Calculate bounding box
        const bounds = bbox(feature)
        const [minLng, minLat, maxLng, maxLat] = bounds

        // Calculate center point
        const centerLng = (minLng + maxLng) / 2
        const centerLat = (minLat + maxLat) / 2

        // Calculate zoom level based on bounding box size
        const lngDiff = maxLng - minLng
        const latDiff = maxLat - minLat
        const maxDiff = Math.max(lngDiff, latDiff)

        // Determine appropriate zoom level (higher zoom for smaller areas)
        let zoom = 10
        if (maxDiff > 1) zoom = 8
        else if (maxDiff > 0.5) zoom = 10
        else if (maxDiff > 0.1) zoom = 12
        else if (maxDiff > 0.01) zoom = 14
        else zoom = 16

        // Update viewport to center on the polygon
        setViewport({
          latitude: centerLat,
          longitude: centerLng,
          zoom,
        })
      } catch (_error) {
        // Keep default viewport if calculation fails
      }
    }
  }, [saleData.boundary_geom])

  // Update boundary geometry whenever features change
  useEffect(() => {
    const polygonFeatures = features.filter(
      (feature: any) =>
        feature.geometry.type === "Polygon" ||
        feature.geometry.type === "MultiPolygon"
    )

    if (polygonFeatures.length === 0) {
      updateBoundary(undefined)
    } else if (polygonFeatures.length === 1) {
      updateBoundary(
        polygonFeatures[0].geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon
      )
    } else {
      // Multiple polygons - combine into a MultiPolygon
      const coordinates: number[][][][] = []

      polygonFeatures.forEach((feature: any) => {
        if (feature.geometry.type === "Polygon") {
          coordinates.push(feature.geometry.coordinates as number[][][])
        } else if (feature.geometry.type === "MultiPolygon") {
          coordinates.push(...(feature.geometry.coordinates as number[][][][]))
        }
      })

      const multiPolygon: GeoJSON.MultiPolygon = {
        type: "MultiPolygon",
        coordinates: coordinates,
      }

      updateBoundary(multiPolygon)
    }
  }, [features, updateBoundary])

  // Following MapVisualization pattern - handle create/update/delete separately
  const handleDrawCreate = (event: any) => {
    const newFeatures = (event.features || []).map((feature: any) => ({
      ...feature,
      properties: {
        ...feature.properties,
        source: "manual",
      },
    }))
    setFeatures((prevFeatures) => [...prevFeatures, ...newFeatures])
  }

  const handleDrawUpdate = (event: any) => {
    const updatedFeatures = event.features || []

    // Update features by replacing ones with matching IDs
    setFeatures((prevFeatures) => {
      const updatedFeatureIds = new Set(
        updatedFeatures.map((f: any) => f.id) as Iterable<unknown>
      )
      const keepFeatures = prevFeatures.filter(
        (f: any) => !updatedFeatureIds.has(f.id)
      )
      return [...keepFeatures, ...updatedFeatures]
    })
  }

  const handleDrawDelete = (event: any) => {
    // event.features contains the features that were DELETED, not the remaining ones
    // Filter them out from current features
    const deletedFeatures = event.features || []
    setFeatures((prevFeatures) =>
      prevFeatures.filter((currFeature) => {
        const wasDeleted = deletedFeatures.find(
          (deletedFeature: any) => deletedFeature.id === currFeature.id
        )
        return !wasDeleted
      })
    )
  }

  const handleFilesSelected = async (files: File[], _metadata: any[]) => {
    setIsProcessing(true)
    setUploadError(null)
    setProcessingStatus("Validating file...")

    try {
      // Process the first file (we only allow single file upload)
      const file = files[0]
      if (!file) {
        throw new Error("No file provided")
      }

      setProcessingStatus("Reading file contents...")

      // Add a small delay to show the status update
      await new Promise((resolve) => setTimeout(resolve, 100))

      setProcessingStatus("Processing spatial data...")

      // Process the spatial file
      const result = await processSpatialFile(file)

      setProcessingStatus("Preparing preview...")

      // Store preview data and show preview modal, close upload modal
      setPreviewData({
        file: file,
        result: result,
        features: result.features,
        metadata: result.metadata,
      })
      setShowUploadModal(false) // Close upload modal first
      setShowPreviewModal(true) // Then show preview modal

      // Clear any previous errors
      setUploadError(null)
    } catch (error: any) {
      // Use our enhanced error formatting
      const errorMessage = error.userMessage
        ? formatErrorForUser(error as SpatialValidationError)
        : `Failed to process spatial file: ${error.message || "Unknown error"}`

      setUploadError(errorMessage)
    } finally {
      setIsProcessing(false)
      setProcessingStatus("")
    }
  }

  const handleFilesRemoved = () => {
    setUploadError(null)
  }

  const handleAddBoundariesToMap = () => {
    if (!previewData) return

    const { features: resultFeatures, metadata } = previewData

    // Convert uploaded features to map features with source tagging and unique IDs
    const mapFeatures = resultFeatures.map((feature: any, index: number) => ({
      type: "Feature" as const,
      geometry: feature.geometry,
      properties: {
        ...feature.properties,
        source: "uploaded",
        originalId: feature.id,
        isUploaded: true, // Additional marker for uploaded features
      },
      id: `uploaded_${Date.now()}_${index}`, // Ensure unique IDs for uploaded features
    }))

    // Add uploaded features to the features array - following MapVisualization pattern
    setFeatures((prevFeatures) => [...prevFeatures, ...mapFeatures])

    // Auto-zoom to fit uploaded boundaries
    if (metadata.boundingBox) {
      const [minLng, minLat, maxLng, maxLat] = metadata.boundingBox

      // Calculate center point
      const centerLng = (minLng + maxLng) / 2
      const centerLat = (minLat + maxLat) / 2

      // Calculate zoom level based on bounding box size
      const lngDiff = maxLng - minLng
      const latDiff = maxLat - minLat
      const maxDiff = Math.max(lngDiff, latDiff)

      // Determine appropriate zoom level with padding
      let zoom = 10
      if (maxDiff > 2) zoom = 7
      else if (maxDiff > 1) zoom = 8
      else if (maxDiff > 0.5) zoom = 10
      else if (maxDiff > 0.1) zoom = 12
      else if (maxDiff > 0.01) zoom = 14
      else zoom = 16

      // Update viewport to center on uploaded boundaries
      setViewport({
        latitude: centerLat,
        longitude: centerLng,
        zoom,
      })
    }

    // Close modals and reset preview data
    setShowPreviewModal(false)
    setShowUploadModal(false)
    setPreviewData(null)

    // Show success message
    const areaInAcres = Math.round(metadata.totalArea / 4047)
    setSuccessMessage(
      `Successfully added ${mapFeatures.length} boundary feature${
        mapFeatures.length > 1 ? "s" : ""
      } (${areaInAcres} acres) to the map!`
    )

    // Clear success message after 5 seconds
    setTimeout(() => {
      setSuccessMessage(null)
    }, 5000)
  }

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false)
    setPreviewData(null)
  }

  const handleOpenUploadModal = () => {
    setShowUploadModal(true)
    setUploadError(null)
  }

  const handleCloseUploadModal = () => {
    setShowUploadModal(false)
    setUploadError(null)
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Define Sale Boundaries
        </h3>
        <p className="text-gray-600 mb-4">
          Draw boundaries manually on the map below or upload spatial files for
          precision.
        </p>
      </div>

      {/* Upload File Button */}
      <div className="flex justify-center mb-6">
        <button
          type="button"
          onClick={handleOpenUploadModal}
          className="px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <span>Upload File</span>
          </div>
        </button>
      </div>

      {/* Success Notification */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <div className="bg-green-50 border border-green-200 rounded-md p-4 shadow-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  {successMessage}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    type="button"
                    onClick={() => setSuccessMessage(null)}
                    className="inline-flex rounded-md p-1.5 text-green-500 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-green-50"
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg
                      className="h-3 w-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drawing Instructions - Always visible */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Drawing Instructions
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              Use the polygon tool on the map below to draw your sale
              boundaries. Click to start drawing, continue clicking to add
              points, and double-click to complete the polygon.
            </p>
          </div>
        </div>
      </div>

      {/* Map Container - Responsive */}
      <div className="bg-gray-100 rounded-lg overflow-hidden">
        <div className="h-64 sm:h-80 md:h-96 lg:h-[500px]">
          <MapGL
            ref={mapRef}
            style={{ width: "100%", height: "100%" }}
            mapStyle={mapStyles.aerial.url}
            accessToken={MAPBOX_TOKEN}
            latitude={viewport.latitude}
            longitude={viewport.longitude}
            zoom={viewport.zoom}
            onViewportChange={setViewport}
          >
            <Draw
              data={{
                type: "FeatureCollection",
                features: features,
              }}
              onDrawCreate={handleDrawCreate}
              onDrawUpdate={handleDrawUpdate}
              onDrawDelete={handleDrawDelete}
              styles={mapVisualizationDrawStyles}
              displayControlsDefault={false}
              controls={{
                polygon: true,
                trash: true,
              }}
            />

            {/* TODO: Add proper uploaded features rendering */}
            {/* For now, uploaded features are combined in the boundary but not visually distinct */}
          </MapGL>
        </div>
      </div>

      {/* Spatial File Preview Modal */}
      {showPreviewModal && previewData && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="bg-white border-2 border-gray-300 shadow-lg rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Boundary Preview
              </h3>
              <button
                type="button"
                onClick={handleClosePreviewModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* File Info */}
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  File Information
                </h4>
                <div className="text-sm text-gray-900 space-y-1">
                  <div>
                    Name:{" "}
                    <span className="font-medium">{previewData.file.name}</span>
                  </div>
                  <div>
                    Size:{" "}
                    <span className="font-medium">
                      {Math.round(previewData.file.size / 1024)} KB
                    </span>
                  </div>
                  <div>
                    Type:{" "}
                    <span className="font-medium">
                      {previewData.file.name.toLowerCase().endsWith(".kml")
                        ? "KML"
                        : "Shapefile"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Boundary Info */}
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Boundary Information
                </h4>
                <div className="text-sm text-gray-900 space-y-1">
                  <div>
                    Features:{" "}
                    <span className="font-medium">
                      {previewData.metadata.featureCount}
                    </span>
                  </div>
                  <div>
                    Total Area:{" "}
                    <span className="font-medium">
                      {Math.round(previewData.metadata.totalArea / 4047)} acres
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 mt-2">
                    Coordinate System: {previewData.metadata.coordinateSystem}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClosePreviewModal}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddBoundariesToMap}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Add to Map
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload File Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="bg-white border-2 border-gray-300 shadow-lg rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Upload your property file
              </h3>
              <button
                type="button"
                onClick={handleCloseUploadModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <SpatialFileUpload
              onFilesSelected={(files, metadata) => {
                void handleFilesSelected(files, metadata)
              }}
              onFilesRemoved={handleFilesRemoved}
              onError={setUploadError}
              error={uploadError}
              isProcessing={isProcessing}
              processingStatus={processingStatus}
            />
          </div>
        </div>
      )}
    </div>
  )
}
